// src/views/RewardsView.jsx - EXCITING REAL-TIME VERSION
import React, { useState, useEffect } from 'react';
import { Wallet, TrendingUp, Award, Zap, Trophy, Target, Rocket, Clock } from 'lucide-react';
import { formatBalance } from '../utils/formatters';

export const RewardsView = ({ 
  isConnected, 
  connectWallet, 
  pogeBalance, 
  pendingRewards, 
  stakedTokens, 
  emissionRate,
  userAchievements,
  loading, 
  claim,
  setView,
  nftBalance
}) => {
  // Live reward counter
  const [liveRewards, setLiveRewards] = useState(parseFloat(pendingRewards));
  const [timeStaked, setTimeStaked] = useState(0);
  
  // Total NFTs owned = staked + unstaked
  const totalNFTs = (stakedTokens.length || 0) + (nftBalance || 0);
  
  // Calculate rates
  const dailyEarnings = parseFloat(emissionRate) * stakedTokens.length * 86400;
  const perSecond = parseFloat(emissionRate) * stakedTokens.length;
  
  // Live ticker - updates every second
  useEffect(() => {
    if (stakedTokens.length === 0) return;
    
    setLiveRewards(parseFloat(pendingRewards)); // Sync with actual
    
    const interval = setInterval(() => {
      setLiveRewards(prev => prev + perSecond);
      setTimeStaked(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [stakedTokens.length, perSecond, pendingRewards]);
  
  // Reset ticker after claim
  useEffect(() => {
    setLiveRewards(parseFloat(pendingRewards));
  }, [pendingRewards]);
  
  // Achievements
  const achievements = [
    { 
      id: 'early', 
      name: 'Early Adopter', 
      unlocked: userAchievements[0] || false,
      desc: 'First 100 stakers',
      icon: Rocket,
      color: 'from-purple-500 to-purple-700'
    },
    { 
      id: 'diamond', 
      name: 'Diamond Paws', 
      unlocked: userAchievements[1] || false,
      desc: '180 days staked',
      icon: Trophy,
      color: 'from-blue-500 to-blue-700'
    },
    { 
      id: 'collector', 
      name: 'Collector', 
      unlocked: userAchievements[2] || false,
      desc: '10+ staked 7 days',
      icon: Target,
      color: 'from-green-500 to-green-700'
    },
    { 
      id: 'whale', 
      name: 'Poge Whale', 
      unlocked: userAchievements[3] || false,
      desc: '10,000+ POGE',
      icon: Zap,
      color: 'from-yellow-500 to-yellow-700'
    }
  ];
  
  const unlockedCount = achievements.filter(a => a.unlocked).length;
  
  // Time formatting
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${m}m ${s}s`;
  };
  
  if (!isConnected) {
    return (
      <div>
        <h2 className="text-3xl md:text-5xl font-black mb-8 text-white uppercase">
          MY $POGE REWARDS
        </h2>
        <div className="text-center py-20 text-xl p-8 bg-gradient-to-br from-black to-gray-900 border-4 border-white/90 shadow-[0_0_20px_rgba(0,255,0,0.2)]">
          <Wallet size={48} className="mx-auto mb-4 opacity-50" />
          <div className="font-bold mb-2">WALLET NOT CONNECTED</div>
          <div className="text-sm opacity-75 mb-4">Connect your wallet to view rewards</div>
          <button 
            onClick={connectWallet}
            className="bg-gradient-to-r from-white to-gray-300 text-black font-bold py-2 px-6 border-2 border-green-400/90 hover:opacity-90 transition-all shadow-[0_0_10px_rgba(0,255,0,0.3)]"
          >
            CONNECT WALLET
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <h2 className="text-3xl md:text-5xl font-black mb-8 text-white uppercase">
        MY $POGE REWARDS
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        
        {/* CLAIM REWARDS CARD */}
        <div className="bg-gradient-to-br from-green-500 to-green-700 text-white p-6 md:p-8 border-4 border-black shadow-[8px_8px_0_0_#000] relative overflow-hidden">
          {/* Animated background pulse */}
          {stakedTokens.length > 0 && (
            <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-transparent animate-pulse pointer-events-none" />
          )}
          
          <h3 className="text-2xl font-black mb-6 flex items-center gap-2 relative z-10">
            <TrendingUp size={28} />
            CLAIM REWARDS
          </h3>
          
          {/* Live Ticker - BIG */}
          <div className="mb-6 text-center relative z-10">
            <div className="text-sm font-bold mb-2 opacity-90 flex items-center justify-center gap-2">
              {stakedTokens.length > 0 && <Clock size={16} className="animate-spin" />}
              PENDING REWARDS
            </div>
            <div 
              className="text-6xl md:text-7xl font-black mb-2 transition-all duration-300" 
              style={{ 
                textShadow: '4px 4px 0 #000',
                color: stakedTokens.length > 0 ? '#fff' : '#ffffff80'
              }}
            >
              {liveRewards.toFixed(4)}
            </div>
            <div className="text-2xl font-bold opacity-90">$POGE</div>
            
            {/* Live earning indicator */}
            {stakedTokens.length > 0 && (
              <div className="mt-3 text-sm font-bold text-green-200 animate-pulse">
                +{perSecond.toFixed(6)} POGE/sec
              </div>
            )}
          </div>

          {/* Claim Button */}
          {parseFloat(pendingRewards) > 0 ? (
            <button 
              onClick={claim} 
              disabled={loading}
              className="w-full bg-white text-green-700 font-black text-2xl py-6 border-4 border-black mb-6 disabled:opacity-50 hover:scale-105 hover:shadow-[0_0_20px_rgba(255,255,255,0.5)] transition-all relative z-10"
            >
              {loading ? 'CLAIMING...' : '💰 CLAIM NOW'}
            </button>
          ) : stakedTokens.length > 0 ? (
            <div className="w-full bg-black/30 text-white font-bold text-center py-6 border-4 border-black/50 mb-6 relative z-10">
              <div className="animate-pulse">⏳ REWARDS ACCUMULATING...</div>
              <div className="text-xs mt-2 opacity-70">Live for {formatTime(timeStaked)}</div>
            </div>
          ) : (
            <button
              onClick={() => setView('stake')}
              className="w-full bg-yellow-400 text-black font-black text-xl py-6 border-4 border-black mb-6 hover:scale-105 transition-all relative z-10"
            >
              🚀 START EARNING NOW
            </button>
          )}

          {/* Quick Stats with progress bars */}
          <div className="space-y-3 relative z-10">
            <div className="p-3 bg-black/30 border-2 border-white/50">
              <div className="flex justify-between mb-2">
                <span className="font-bold">CLAIMED POGE:</span>
                <span className="text-xl font-black">{formatBalance(pogeBalance)}</span>
              </div>
              {/* Progress to whale */}
              {parseFloat(pogeBalance) < 10000 && (
                <div className="w-full h-2 bg-black/50 border border-white/30">
                  <div 
                    className="h-full bg-yellow-400 transition-all duration-500"
                    style={{ width: `${Math.min((parseFloat(pogeBalance) / 10000) * 100, 100)}%` }}
                  />
                </div>
              )}
            </div>
            
            <div className="p-3 bg-black/30 border-2 border-white/50">
              <div className="flex justify-between">
                <span className="font-bold">DAILY RATE:</span>
                <span className="text-xl font-black text-yellow-300">+{dailyEarnings.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="p-3 bg-black/30 border-2 border-white/50">
              <div className="flex justify-between mb-2">
                <span className="font-bold">EARNING NFTs:</span>
                <span className="text-xl font-black">{stakedTokens.length}/{totalNFTs}</span>
              </div>
              {/* Staking progress bar */}
              {totalNFTs > 0 && (
                <div className="w-full h-2 bg-black/50 border border-white/30">
                  <div 
                    className="h-full bg-green-400 transition-all duration-500"
                    style={{ width: `${(stakedTokens.length / totalNFTs) * 100}%` }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Manage Button */}
          <button 
            onClick={() => setView('stake')}
            className="w-full mt-6 bg-black text-white font-bold py-3 border-2 border-white hover:bg-gray-900 transition-all relative z-10"
          >
            ⚡ MANAGE STAKING
          </button>
        </div>
        
        {/* ACHIEVEMENTS CARD */}
        <div className="bg-white text-black p-6 md:p-8 border-4 border-black shadow-[8px_8px_0_0_#000]">
          <h3 className="text-2xl font-black mb-6 flex items-center gap-2">
            <Award size={28} />
            ACHIEVEMENTS
          </h3>

          {/* Progress */}
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-100 to-blue-100 border-2 border-black">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-sm">PROGRESS</span>
              <span className="font-black text-lg">{unlockedCount}/4</span>
            </div>
            <div className="h-4 bg-gray-300 border-2 border-black overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-1000"
                style={{ width: `${(unlockedCount / 4) * 100}%` }}
              />
            </div>
          </div>

          {/* Achievement Grid with animations */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {achievements.map((achievement, idx) => {
              const Icon = achievement.icon;
              return (
                <div 
                  key={achievement.id}
                  className={`p-4 border-4 transition-all duration-300 ${
                    achievement.unlocked 
                      ? `bg-gradient-to-br ${achievement.color} text-white border-black hover:scale-105 shadow-lg` 
                      : 'bg-gray-200 border-gray-400 opacity-60 hover:opacity-80'
                  }`}
                  style={{ 
                    animation: achievement.unlocked ? `slideIn 0.5s ease-out ${idx * 0.1}s both` : 'none'
                  }}
                >
                  <div className="flex justify-center mb-2">
                    <Icon 
                      size={32} 
                      className={achievement.unlocked ? 'animate-bounce' : 'text-gray-400'}
                    />
                  </div>
                  <div className={`font-black text-xs text-center mb-1 ${achievement.unlocked ? 'text-white' : ''}`}>
                    {achievement.name}
                  </div>
                  <div className={`text-xs text-center ${achievement.unlocked ? 'text-white/90' : 'opacity-75'}`}>
                    {achievement.desc}
                  </div>
                  <div className="text-center mt-2">
                    {achievement.unlocked ? (
                      <span className="text-yellow-300 font-black text-sm drop-shadow-lg">✓ UNLOCKED</span>
                    ) : (
                      <span className="text-gray-400 font-bold text-xs">LOCKED</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stats */}
          <div className="space-y-3">
            <div className="p-3 bg-gray-100 border-2 border-black">
              <div className="flex justify-between items-center mb-1">
                <span className="font-bold text-sm">TOTAL OWNED:</span>
                <span className="text-xl font-black">{totalNFTs}</span>
              </div>
              <div className="flex justify-between text-xs opacity-60">
                <span>{stakedTokens.length} staked</span>
                <span>{nftBalance} in wallet</span>
              </div>
            </div>
            <div className="p-3 bg-gray-100 border-2 border-black">
              <div className="flex justify-between items-center">
                <span className="font-bold text-sm">PEAK STAKED:</span>
                <span className="text-xl font-black text-purple-600">
                  {Number(userAchievements[4] || 0)}
                </span>
              </div>
            </div>
            <div className="p-3 bg-gray-100 border-2 border-black">
              <div className="flex justify-between items-center">
                <span className="font-bold text-sm">LIFETIME EARNED:</span>
                <span className="text-xl font-black text-green-600">
                  {formatBalance((Number(userAchievements[5] || 0) / 1e18).toString())}
                </span>
              </div>
            </div>
          </div>

          {/* Encouragement with animations */}
          {unlockedCount < 4 && totalNFTs > 0 && (
            <div className="mt-6 p-4 bg-yellow-100 border-2 border-yellow-400 text-center animate-pulse">
              <div className="font-black text-sm mb-1">
                {stakedTokens.length === 0 
                  ? '🎯 START YOUR JOURNEY' 
                  : unlockedCount === 0 
                    ? `💪 ${4 - unlockedCount} ACHIEVEMENTS TO UNLOCK`
                    : `🔥 ${4 - unlockedCount} MORE TO GO`
                }
              </div>
              <div className="text-xs opacity-75">
                {stakedTokens.length === 0 
                  ? 'Unlock achievements by staking your NFTs' 
                  : unlockedCount === 0
                    ? 'You\'re earning! Keep going to unlock achievements'
                    : 'Keep staking to unlock more achievements'
                }
              </div>
            </div>
          )}

          {unlockedCount === 4 && (
            <div className="mt-6 p-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white border-2 border-black text-center animate-bounce">
              <div className="font-black text-lg mb-1">🏆 LEGEND STATUS</div>
              <div className="text-sm opacity-90">All achievements unlocked!</div>
            </div>
          )}
        </div>
      </div>
      
      {/* Add keyframes for slideIn animation */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};